'use client';

import { useEffect, useRef, useState } from 'react';

import { useSetAtom } from 'jotai';
import { z } from 'zod';

import {
  ActivityStore,
  messageFormSchema,
  mobileAppEmbedUrlAtom,
  useSetIframeUrl,
  type ResponderPanel,
} from '@/shared-survey';
import { navigateTopLevel } from '@/shared-utilities';
import {
  Channel,
  GetProjectQuery,
  InitiateSurveyV3Mutation,
  ProjectStatus,
} from '@/survey-graphql';

import type { ProjectResponseDeliveryGate } from './project-response-delivery-gate.types';
import { useProjectResponseHandler } from './project-response-handler.hooks';
import {
  useInitialMessageFromProject,
  useProjectResponseMessages,
} from './project-response-messages.hooks';
import { useProjectResponseQueries } from './project-response-queries.hooks';
import { useProjectResponseTTS } from './project-response-tts.hooks';
import { NIL_UUID } from './project-response.consts';
import {
  getResumeTtsMessage,
  resolveCompletionRedirectUrl,
} from './project-response.helpers';
import { useUsabilityRecordingDuration } from './project-usability-recording.hooks';

export type MessageFormValues = z.infer<typeof messageFormSchema>;

interface UseSurveyResponseProps {
  project: GetProjectQuery['project'];
  responderEmail?: string;
  channel?: Channel;
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  sessionId: string;
  panel: ResponderPanel | null;
  onTranscriptAnswerError?: () => void;
  onSurveyComplete?: () => void;
  enableSubmittedScreen?: boolean;
  /** Resume an existing response (widget UT after a host-page navigation) */
  initialInsightId?: string | null;
  deliveryGate?: ProjectResponseDeliveryGate;
  enableLegacyPollResults?: boolean;
}

export function useProjectResponse({
  project,
  responderEmail,
  channel,
  panel,
  sessionId,
  firstQuestion,
  onTranscriptAnswerError,
  onSurveyComplete,
  enableSubmittedScreen = false,
  initialInsightId = null,
  deliveryGate,
  enableLegacyPollResults = true,
}: UseSurveyResponseProps) {
  const isSubmittingRef = useRef(false);
  const activityStore = useRef<ActivityStore>({
    lastContentUpdateAt: null,
    notifyContentUpdate() {
      this.lastContentUpdateAt = Date.now();
    },
  });
  const conversationOverRef = useRef<string | null>(null);
  const isResumeRef = useRef(initialInsightId != null);
  const hasPrimedResumeTtsRef = useRef(false);
  const redirectUrlRef = useRef(project.redirectUrl || null);
  const redirectActionRef = useRef<'standard' | 'new-window' | 'new-tab'>(
    'standard'
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [insightId, setInsightId] = useState<string | null>(initialInsightId);
  const [isInsightChecked, setIsInsightChecked] = useState(false);
  const [checkedInitialTranscript, setCheckedInitialTranscript] = useState<
    string | null
  >(null);
  const [isPublished, setIsPublished] = useState(
    project.status === ProjectStatus.Published
  );
  const [showSubmittedScreen, setShowSubmittedScreen] = useState(false);
  const [hasRuntimeRedirect, setHasRuntimeRedirect] = useState(false);

  const {
    getSpeech,
    getSpeechForMessages,
    speechAudios,
    replaceMessageId,
    audioQueue,
  } = useProjectResponseTTS(project.isAiVoiceEnabled ?? false, project.id);
  const { insightData, isPollResultLoading, pollResultData, insightLoading } =
    useProjectResponseQueries(
      project,
      insightId,
      isPublished,
      () => setIsPublished(false),
      enableLegacyPollResults
    );
  const setIframeUrl = useSetIframeUrl();
  const setMobileAppEmbedUrl = useSetAtom(mobileAppEmbedUrlAtom);
  const { getCurrentRecordingTime } = useUsabilityRecordingDuration();
  const initialMessageFromProject = useInitialMessageFromProject(
    sessionId,
    firstQuestion,
    project.language
  );
  const { handleAddMessage, isSaving, replyFeedbackIds, resetAudioRecord } =
    useProjectResponseHandler({
      project,
      firstQuestion,
      sessionId,
      channel,
      panel,
      speechAudiosData: {
        getSpeech,
        getSpeechForMessages,
        speechAudios,
        replaceMessageId,
        audioQueue,
      },
      insightData,
      initialMessageFromProject,
      redirectUrlRef,
      conversationOverRef,
      redirectActionRef,
      setInsightId,
      responderEmail,
      setIframeUrl,
      setMobileAppEmbedUrl,
      getCurrentRecordingTime,
      setIsPublished,
      insightId,
      activityStore,
      onTranscriptAnswerError,
      onSurveyComplete,
      deliveryGate,
      onRuntimeRedirectSet: () => setHasRuntimeRedirect(true),
    });
  const { messages } = useProjectResponseMessages({
    sessionId,
    firstQuestion,
    insightData,
    project,
    pollResultData,
    isPollResultLoading,
    speechAudios,
  });
  const estimatedTotalTimeInSeconds = project.estimatedCompletionTime ?? 0;

  const isConversationOver = (() => {
    return !!(
      insightData?.insightV2.messages?.length &&
      insightData?.insightV2.messages.some(
        (m) => m.metadata?.isConversationOver
      )
    );
  })();

  const handleSubmit = async (values: MessageFormValues) => {
    if (isSubmitting || isSubmittingRef.current) return;
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    try {
      await handleAddMessage(values);
      activityStore.current.lastContentUpdateAt = null;
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const initialTranscript = initialMessageFromProject.transcript ?? '';
  if (initialTranscript !== checkedInitialTranscript) {
    setCheckedInitialTranscript(initialTranscript);
    setIsInsightChecked(true);
  }

  useEffect(() => {
    // On resume the first question is already answered; speaking it would voice
    // a stale question. The current question is primed by the effect below once
    // the seeded insight loads.
    if (isResumeRef.current) return;
    void getSpeech(NIL_UUID, initialTranscript);
  }, [getSpeech, initialTranscript]);

  useEffect(() => {
    if (hasPrimedResumeTtsRef.current || !isResumeRef.current) return;
    const resumeTtsMessage = getResumeTtsMessage(
      insightData?.insightV2.messages
    );
    if (!resumeTtsMessage) return;
    hasPrimedResumeTtsRef.current = true;
    void getSpeech(resumeTtsMessage.id, resumeTtsMessage.text);
  }, [getSpeech, insightData]);

  const canRedirect = !!project.redirectUrl || !!panel || hasRuntimeRedirect;
  const getCompletionRedirectUrl = (): string | null =>
    resolveCompletionRedirectUrl({
      isConversationOver,
      rawUrl: redirectUrlRef.current,
    });
  const handleConversationOver =
    canRedirect || enableSubmittedScreen
      ? () => {
          const url = getCompletionRedirectUrl();
          if (url) {
            if (redirectActionRef.current === 'new-window') {
              window.open(url);
            } else if (redirectActionRef.current === 'new-tab') {
              window.open(url, '_blank');
            } else {
              navigateTopLevel(url);
            }
          } else if (enableSubmittedScreen) {
            setShowSubmittedScreen(true);
          }
        }
      : undefined;

  return {
    isPublished,
    markProjectUnpublished: () => setIsPublished(false),
    isSaving,
    isSubmitting,
    messages,
    handleSubmit,
    insightLoading: !isInsightChecked || insightLoading,
    isConversationOver,
    progress: insightData?.insightV2.progress ?? 0,
    handleConversationOver,
    getCompletionRedirectUrl,
    showSubmittedScreen,
    estimatedTotalTimeInSeconds,
    insightId,
    conversationOverRef,
    activityStore,
    getSpeech,
    speechAudios,
    replyFeedbackIds,
    resetAudioRecord,
  };
}
